import React, { useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { ADDRESS, CREATED_ON, DELETE_PARTY_PERMISSION_MESSAGE, NO_OF_DOMAINS, PARTY_NAME, PARTY_TYPE, UPDATE_PARTY_PERMISSION_MESSAGE } from '../Utils/Constants';
import { TParty, formatDate } from '../Types/party.types';
import { CopyIcon, DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import Table from 'Components/DesignSystem/Table/Table';
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Menu from 'Components/DesignSystem/Menu/Menu';
import ConfirmDialog from '../Components/ConfirmDialog';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { TableColumn } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { AppContext } from 'Context/AppContext';

import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

type PartyTableProps = {
    parties: TParty[];
    onEditParty?: (party: TParty, index: number) => void;
    showLoader?: boolean;
    sortBy?: string;
    sortOrder?: string;
    onSortChange?: (column: string) => void;
    onPartyDeleted?: () => void;
    deletePermission: any;
    updatePermission: any;
    onNavigateToDomain: (party: TParty) => void
}

/**
 * The party grid on the design-system `Table` (§ 118), following
 * `Pages/GMS/List/ListTable.tsx` — the pattern already proven for a list with a row
 * overflow menu (§ 81).
 *
 * What the hand-rolled table was, and why each piece had to go rather than be
 * restyled:
 *
 * - **`Common/components/TableCell` hardcodes `data-testid="components-tablecell-td-1"`**
 *   on every `<td>` it renders. Six columns over five rows is **thirty elements
 *   sharing one testid** — a spec cannot address a cell at all. It is one of the
 *   known offenders named in `CLAUDE.md`, and it is still in use by the Domain and
 *   Channel tables, so it is left in place for them; this table just stops using it.
 * - **Sorting was `<th onClick>`** — not focusable, not keyboard-operable, and with
 *   no `aria-sort`. `TableHeader` gives each sortable column a real button and the
 *   `aria-sort` state (SC 2.1.1 / 4.1.2).
 * - **The sort glyphs were FontAwesome `<i class="fa-sort">`**, drawn by the
 *   `use.fontawesome.com` CDN stylesheet in `index.html`. The sort affordance on this
 *   page depended on a third-party host being reachable; `TableHeader`'s indicator is
 *   an inline SVG.
 * - **`ConfirmationDialog` was a bare `<div>`** — no `role="dialog"`, no `aria-modal`,
 *   no focus trap, no focus restore, no Escape. `Dialog` supplies all five, and the
 *   `tone="danger"` glyph marks the confirm as destructive (§ 53).
 * - **The `<tr>` carried `position: relative` and the actions `<td>`
 *   `overflow: visible`** purely so the old absolutely-positioned menu could escape
 *   the scroll container. The `Menu` portals to `<body>` instead, so both hacks are
 *   gone rather than carried across.
 *
 * Two details worth knowing before you change anything here:
 *
 * - **Sort is fully controlled and two-state.** The parent's `onSortChange` flips
 *   asc/desc itself, so the DS header's own asc→desc→null cycle never applies: the
 *   direction it suggests is ignored and only the column id is forwarded. The four
 *   sortable column ids ARE the backend's `sort_by` values (`name`, `party_type`,
 *   `industry_count`, `created_at`) — renaming one silently changes the request.
 * - **`Address` is not sortable, and now looks it.** It wore `table-header-style`,
 *   whose `tw-cursor-pointer` is unconditional, so it showed a pointer cursor and did
 *   nothing on click.
 *
 * Behaviour is unchanged throughout: the same delete endpoint, the same four toast
 * messages including the `dissassociate` follow-up, `refreshParties()` before
 * `onPartyDeleted()`, the permission guards on Edit and Delete, and the
 * sort-while-loading block.
 */
const PartyTable = ({ parties, onEditParty, showLoader = false, sortBy = 'name', sortOrder = 'asc', onSortChange, onPartyDeleted, deletePermission, updatePermission, onNavigateToDomain }: PartyTableProps) => {
    const appContext = useContext(AppContext);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; partyIndex: number | null; partyName: string; partyId: string | null;}>({ isOpen: false, partyIndex: null, partyName: '', partyId: null});
    const [isDeleting, setIsDeleting] = useState(false);
    const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
    /* `right` rather than `left`: the menu hangs leftward from the kebab, so anchoring
       its right edge keeps it on-screen at any table width. */
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

    useEffect(() => {
        /* A fixed-position menu detaches from its row as soon as anything scrolls, so
           scrolling closes it. Outside-click and Escape are the Menu's own job. */
        function handleScroll() {
            setOpenMenuFor(null);
        }
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const handleColumnClick = (column: string) => {
        if (onSortChange && !showLoader) {
            onSortChange(column);
        }
    };

    const handleCopyId = (party: TParty) => {
        navigator.clipboard.writeText(party.id).then(() => {
            toast.success('Party ID copied to clipboard!');
        }).catch(() => {
            toast.error('Failed to copy Party ID');
        });
    };

    const handleEdit = (party: TParty, index: number) => {
        if (!updatePermission) {
            toast.warning(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        if (onEditParty) {
            onEditParty(party, index);
        }
    };

    const handleDeleteClick = (party: TParty, index: number) => {
        if (!deletePermission) {
            toast.warning(DELETE_PARTY_PERMISSION_MESSAGE);
            return;
        }
        setDeleteConfirmation({
            isOpen: true,
            partyIndex: index,
            partyName: party.name,
            partyId: party.id
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation.partyId) return;
        
        setIsDeleting(true);
        
        try {
            const deleteResponse = await ServiceWrapper.doGet(`${api_base_url}party/delete/${deleteConfirmation.partyId}`,null,null,AuthManager.getGmsToken(),);
            const response = deleteResponse?.data;
            if (response && response.done) {
                toast.success('Party deleted successfully!');
                await appContext.refreshHierarchy.refreshParties();
                if (onPartyDeleted) {
                    onPartyDeleted();
                }
            } else {
                const errorMessage = response?.message || 'Failed to delete party';
                toast.error(errorMessage);
                
                if (errorMessage.includes('dissassociate')) {
                    toast.info('Please remove all associated domains before deleting this party.', {
                        // react-toastify's `autoClose` is `duration` here (§ 116).
                        duration: 5000
                    });
                }
            }
        } catch (error) {
            console.error('Error deleting party:', error);
            toast.error('An error occurred while deleting the party. Please try again.');
        } finally {
            setIsDeleting(false);
            setDeleteConfirmation({
                isOpen: false,
                partyIndex: null,
                partyName: '',
                partyId: null
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation({
            isOpen: false,
            partyIndex: null,
            partyName: '',
            partyId: null
        });
    };

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, partyId: string) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        setOpenMenuFor(prev => (prev === partyId ? null : partyId));
    };

    const getMenuItems = (party: TParty, index: number): MenuItem[] => [
        {
            id: `copy-id-${index}`,
            label: 'Copy ID',
            icon: <CopyIcon />,
            'data-testid': `party-table-menu-copy-${party.id}`,
            onClick: () => { setOpenMenuFor(null); handleCopyId(party); },
        },
        {
            id: `edit-${index}`,
            label: 'Edit',
            icon: <EditIcon />,
            'data-testid': `party-table-menu-edit-${party.id}`,
            onClick: () => { setOpenMenuFor(null); handleEdit(party, index); },
        },
        /*
          A SEPARATE entry, not `divider: true` on the Delete item — and this bit me.
          The legacy `DropdownMenu` read `divider` as a MODIFIER: it drew a rule above
          the item and then rendered the item. The DS `Menu` reads it as a standalone
          ENTRY: `item.divider ? <rule/> : <button/>` — the flag replaces the item
          outright, taking its label, icon, testid and `onClick` with it. Porting the
          flag verbatim therefore shipped a party table with **no Delete action at
          all**, with a clean typecheck. `MenuItem.label` being optional is the clue
          that a divider was always meant to be its own entry.
        */
        { id: `divider-before-delete-${index}`, divider: true },
        {
            id: `delete-${index}`,
            label: 'Delete',
            icon: <DeleteIcon />,
            'data-testid': `party-table-menu-delete-${party.id}`,
            onClick: () => { setOpenMenuFor(null); handleDeleteClick(party, index); },
        },
    ];

    const renderRowActions = (party: TParty, rowIndex: number) => (
        <>
            <button
                type="button"
                aria-label="More options"
                aria-haspopup="menu"
                aria-expanded={openMenuFor === party.id}
                /* The legacy `id` is kept verbatim — index-templated and all — because
                   specs in this app key off `id` as often as `data-testid`. The testid
                   moves to the party's own id, which is what the repo rule asks for:
                   `party-partytable-button-1-${index}` renamed rows the moment a sort
                   or a page change reordered them. */
                id={`overflow-btn-${rowIndex}`}
                data-testid={`party-table-overflow-${party.id}`}
                className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface-hover-strong"
                onClick={(e) => handleOverflowClick(e, party.id)}
            >
                <OverFlowIcon />
            </button>
            {openMenuFor === party.id ? ReactDOM.createPortal(
                <div
                    className="tw-fixed tw-z-[9999] tw-w-48"
                    style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                >
                    <Menu
                        variant="compact"
                        items={getMenuItems(party, rowIndex)}
                        data-testid={`party-table-menu-${party.id}`}
                        onDismiss={() => setOpenMenuFor(null)}
                    />
                </div>,
                document.body
            ) : null}
        </>
    );

    const columns: TableColumn<TParty>[] = [
        {
            id: 'name',
            header: PARTY_NAME,
            accessor: 'name',
            width: '20%',
            sortable: true,
            render: (_value, party, rowIndex) => (
                <span
                    role="button"
                    tabIndex={0}
                    /* The legacy `<td>` id, preserved so an id-keyed lookup still
                       resolves to the element carrying the name. */
                    id={`party-name-${rowIndex}`}
                    data-testid={`party-table-name-${party.id}`}
                    /* accent-bold, not accent: resting accent is #030562, which beside
                       the cells' #172b4d ink reads as plain text rather than a link. */
                    className="tw-text-accent-bold hover:tw-underline tw-cursor-pointer"
                    onClick={() => onNavigateToDomain(party)}
                    onKeyDown={(e) => {
                        /* The legacy cell was a click-only `<td>`: the one route into
                           domain management could not be reached from the keyboard. */
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onNavigateToDomain(party);
                        }
                    }}
                >
                    {party.name}
                </span>
            ),
        },
        {
            id: 'address',
            header: ADDRESS,
            accessor: (party: TParty) => party.address || '-',
            width: '20%',
            render: (value, party, rowIndex) => (
                <span id={`address-${rowIndex}`} data-testid={`party-table-address-${party.id}`}>{value}</span>
            ),
        },
        {
            id: 'party_type',
            header: PARTY_TYPE,
            accessor: (party: TParty) => party.party_type || '-',
            width: '18%',
            sortable: true,
            render: (value, party, rowIndex) => (
                <span id={`party-type-${rowIndex}`} data-testid={`party-table-type-${party.id}`}>{value}</span>
            ),
        },
        {
            id: 'industry_count',
            header: NO_OF_DOMAINS,
            accessor: 'industry_count',
            width: '20%',
            sortable: true,
            cellType: 'numerical',
            render: (value, party, rowIndex) => (
                <span id={`no-of-domains-${rowIndex}`} data-testid={`party-table-domains-${party.id}`}>{value}</span>
            ),
        },
        {
            id: 'created_at',
            header: CREATED_ON,
            accessor: (party: TParty) => formatDate(party.created_at),
            sortable: true,
            cellType: 'meta',
            render: (value, party, rowIndex) => (
                <span id={`created-on-${rowIndex}`} data-testid={`party-table-created-${party.id}`}>{value}</span>
            ),
        },
        {
            id: 'actions',
            header: '',
            accessor: () => null,
            width: '5%',
            align: 'right',
            render: (_value, party, rowIndex) => renderRowActions(party, rowIndex),
        },
    ];

    return (
        <>
            <div className="tw-mt-3">
                <Table<TParty>
                    /* Bound the grid so <main> stops scrolling the page — see
                       `pageGridHeight.ts` for the measured reserve. `stickyHeader`
                       needs it: sticky resolves against a scrolling ancestor. */
                    stickyHeader
                    maxHeight={PAGE_GRID_MAX_HEIGHT}
                    columns={columns}
                    data={parties ?? []}
                    rowKey="id"
                    striped
                    loading={showLoader}
                    sortState={{ columnId: sortBy, direction: sortOrder === 'asc' ? 'asc' : 'desc' }}
                    onSort={(columnId) => handleColumnClick(columnId)}
                    emptyContent={
                        /* No testid here: `Table` already applies
                           `${testid}-empty` to the empty `<td>` that wraps this. */
                        <Typography variant="body-primary" className="tw-text-ink-secondary">
                            No parties found
                        </Typography>
                    }
                    id="party-table"
                    data-testid="party-table"
                />
            </div>

            <ConfirmDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Party"
                message={`Are you sure you want to delete the party "${deleteConfirmation.partyName}"?`}
                secondaryMessage="This action cannot be undone."
                tone="danger"
                isLoading={isDeleting}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                data-testid="party-delete-confirm"
            />
        </>
    );
}

export default PartyTable;
